import { Box, Skeleton } from '@mui/material'
import { blueGrey } from '@mui/material/colors'

const classes = {
    actionsBar: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 2,
        height: 56,
    },
    itemContainer: {
        backgroundColor: 'accordionSummary.main',
        border: (theme) => `1px solid ${theme.palette.grey[400]}`,
        borderRadius: 2,
        height: 74,
        padding: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    thumbnail: {
        height: 50,
        width: 50,
        borderRadius: 1,
    },
}

/**
 * TbxQuoteItemListSkeleton - Loading skeleton for QuoteItemList component
 *
 * Displays skeleton placeholders matching the quote item list structure:
 * - Actions bar with selection controls and filters
 * - 3 collapsed item rows with thumbnails and pricing columns
 *
 * @returns {JSX.Element}
 */
const TbxQuoteItemListSkeleton = () => {
    // Generate 3 fixed item rows
    const items = Array.from({ length: 4 }, (_, index) => index)

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Actions bar skeleton */}
            <Box sx={classes.actionsBar}>
                {/* Left section - Select controls and action buttons */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Skeleton
                        height={24}
                        sx={{ borderRadius: '4px' }}
                        variant="rectangular"
                        width={24}
                    />
                    <Skeleton
                        height={20}
                        sx={{ borderRadius: '4px' }}
                        variant="rectangular"
                        width={120}
                    />
                    <Skeleton
                        height={36}
                        sx={{ borderRadius: '4px' }}
                        variant="rectangular"
                        width={80}
                    />
                    <Skeleton
                        height={36}
                        sx={{ borderRadius: '4px' }}
                        variant="rectangular"
                        width={90}
                    />
                </Box>

                {/* Right section - Sort, filter, and toggle controls */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Skeleton
                        height={40}
                        sx={{ borderRadius: '4px' }}
                        variant="rectangular"
                        width={120}
                    />
                    <Skeleton
                        height={40}
                        sx={{ borderRadius: '4px' }}
                        variant="rectangular"
                        width={120}
                    />
                    <Skeleton
                        height={40}
                        sx={{ borderRadius: '4px' }}
                        variant="rectangular"
                        width={140}
                    />
                    <Skeleton
                        height={40}
                        sx={{ borderRadius: '4px' }}
                        variant="rectangular"
                        width={48}
                    />
                </Box>
            </Box>

            {/* Item skeletons */}
            {items.map((itemIndex) => (
                <Box
                    key={`skeleton-item-${itemIndex}`}
                    sx={classes.itemContainer}
                >
                    {/* Left section - Index, drag handle, checkbox, thumbnail, name */}
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <Skeleton
                            height={20}
                            sx={{ borderRadius: '4px' }}
                            variant="rectangular"
                            width={20}
                        />
                        <Skeleton
                            height={24}
                            sx={{ borderRadius: '4px' }}
                            variant="rectangular"
                            width={24}
                        />
                        <Skeleton
                            height={24}
                            sx={{ borderRadius: '4px' }}
                            variant="rectangular"
                            width={24}
                        />
                        <Skeleton
                            sx={classes.thumbnail}
                            variant="rectangular"
                        />
                        <Skeleton
                            height={24}
                            sx={{ borderRadius: '4px' }}
                            variant="rectangular"
                            width={200}
                        />
                    </Box>

                    {/* Right section - Pricing columns and action button */}
                    <Box sx={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {/* Material column */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 120 }}>
                            <Skeleton
                                height={14}
                                sx={{ borderRadius: '4px' }}
                                variant="rectangular"
                                width={80}
                            />
                            <Skeleton
                                height={20}
                                sx={{ borderRadius: '4px' }}
                                variant="rectangular"
                                width={100}
                            />
                        </Box>

                        {/* Quantity column */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 80 }}>
                            <Skeleton
                                height={14}
                                sx={{ borderRadius: '4px' }}
                                variant="rectangular"
                                width={60}
                            />
                            <Skeleton
                                height={20}
                                sx={{ borderRadius: '4px' }}
                                variant="rectangular"
                                width={40}
                            />
                        </Box>

                        {/* Unit price column */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 90 }}>
                            <Skeleton
                                height={14}
                                sx={{ borderRadius: '4px' }}
                                variant="rectangular"
                                width={70}
                            />
                            <Skeleton
                                height={20}
                                sx={{ borderRadius: '4px' }}
                                variant="rectangular"
                                width={80}
                            />
                        </Box>

                        {/* Line price column */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 90 }}>
                            <Skeleton
                                height={14}
                                sx={{ borderRadius: '4px' }}
                                variant="rectangular"
                                width={70}
                            />
                            <Skeleton
                                height={20}
                                sx={{ borderRadius: '4px' }}
                                variant="rectangular"
                                width={80}
                            />
                        </Box>

                        {/* Delete button */}
                        <Skeleton
                            height={40}
                            sx={{ borderRadius: '4px' }}
                            variant="rectangular"
                            width={40}
                        />
                    </Box>
                </Box>
            ))}
        </Box>
    )
}

export default TbxQuoteItemListSkeleton
